import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  rejectUnknownKeys,
  validateEnum,
  validateOptionalBoolean,
  validateOptionalCallback,
  validateOptionalFiniteNumber,
  validateOptionalString,
  validateStringArray,
} from './elementFields.validation';
import { FM_STORY_MAP_LEGEND_FEATURES } from './elementMedia.types';
import { validateMapPresentation } from './mapPresentation.validation';
import {
  FM_MAP_SELECTED_FEATURE_KINDS,
  FM_MEASUREMENT_PLOT_MAPPING_STATUSES,
} from '@/shared/config/mapSlotContracts';
import {
  MEASUREMENT_MAP_SLOT_KEYS,
  REPORT_RANKED_SPATIAL_MAP_SLOT_KEY,
  WORKSPACE_PRIMARY_MAP_SLOT_KEY,
} from '@/shared/config/mapSlotRegistry';

const PRIMARY_MAP_DATA_SLOT_KEYS = new Set<string>([
  WORKSPACE_PRIMARY_MAP_SLOT_KEY,
  ...MEASUREMENT_MAP_SLOT_KEYS,
]);

function validatePrimaryMapData(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'activePropertyId',
      'propertyForestContext',
      'propertyForestContextLoading',
      'riskOverlay',
      'riskInventoryRevision',
      'restoredSelection',
      'restoredViewport',
      'visualizationData',
      'measurementPlotOverlay',
      'onViewportChange',
    ],
    label,
  );
  if (value.activePropertyId !== null)
    assertNonEmptyString(value.activePropertyId, `${label}.activePropertyId`);
  if (typeof value.propertyForestContextLoading !== 'boolean') {
    throw new Error(`Invalid descriptor: ${label}.propertyForestContextLoading must be a boolean.`);
  }
  [
    'propertyForestContext',
    'riskOverlay',
    'restoredSelection',
    'restoredViewport',
    'visualizationData',
  ].forEach((key) => {
    if (value[key] !== null) assertRecord(value[key], `${label}.${key}`);
  });
  if (value.riskInventoryRevision !== null) {
    assertNonEmptyString(value.riskInventoryRevision, `${label}.riskInventoryRevision`);
  }
  if (value.measurementPlotOverlay !== undefined && value.measurementPlotOverlay !== null) {
    assertRecord(value.measurementPlotOverlay, `${label}.measurementPlotOverlay`);
  }
  if (typeof value.onViewportChange !== 'function') {
    throw new Error(`Invalid descriptor: ${label}.onViewportChange must be a callback.`);
  }
}

function validateMapPreviewData(
  value: Record<string, unknown>,
  label: string,
  requiresMapData: boolean,
): void {
  validateOptionalBoolean(value, 'preview', label);
  if (!requiresMapData) {
    if (hasOwn(value, 'mapData')) {
      throw new Error(
        `Invalid descriptor: ${label}.mapData is only valid for a primary governed map slot.`,
      );
    }
    return;
  }
  if (value.preview === true) {
    if (!hasOwn(value, 'mapData') || value.mapData !== null) {
      throw new Error(
        `Invalid descriptor: ${label}.mapData must be explicit null for a deterministic preview.`,
      );
    }
    return;
  }
  if (!hasOwn(value, 'mapData')) {
    throw new Error(`Invalid descriptor: ${label}.mapData is required for a production map.`);
  }
  validatePrimaryMapData(value.mapData, `${label}.mapData`);
}

function validateRankedSpatialMapData(value: unknown, label: string): void {
  if (value === null) return;
  assertRecord(value, label);
  rejectUnknownKeys(value, ['propertyId', 'records', 'revision'], label);
  assertNonEmptyString(value.propertyId, `${label}.propertyId`);
  assertNonEmptyString(value.revision, `${label}.revision`);
  assertArray(value.records, `${label}.records`);
  const recordIds = new Set<string>();
  const geometryRefs = new Set<string>();
  value.records.forEach((record, index) => {
    const recordLabel = `${label}.records[${index}]`;
    assertRecord(record, recordLabel);
    rejectUnknownKeys(
      record,
      ['id', 'label', 'geometryRef', 'sourceId', 'value', 'level'],
      recordLabel,
    );
    assertNonEmptyString(record.id, `${recordLabel}.id`);
    assertNonEmptyString(record.label, `${recordLabel}.label`);
    if (record.geometryRef !== null) {
      assertNonEmptyString(record.geometryRef, `${recordLabel}.geometryRef`);
    }
    assertNonEmptyString(record.sourceId, `${recordLabel}.sourceId`);
    if (recordIds.has(record.id)) {
      throw new Error(`Invalid descriptor: ${recordLabel}.id must be unique.`);
    }
    if (record.geometryRef !== null && geometryRefs.has(record.geometryRef)) {
      throw new Error(`Invalid descriptor: ${recordLabel}.geometryRef must be unique.`);
    }
    recordIds.add(record.id);
    if (record.geometryRef !== null) geometryRefs.add(record.geometryRef);
    if (
      record.value !== null &&
      (typeof record.value !== 'number' || !Number.isFinite(record.value))
    ) {
      throw new Error(`Invalid descriptor: ${recordLabel}.value must be a finite number or null.`);
    }
    if (record.value === null && record.level !== undefined) {
      throw new Error(
        `Invalid descriptor: ${recordLabel}.level must be absent when value is null.`,
      );
    }
    if (record.value !== null) {
      validateEnum(
        record.level,
        ['low', 'moderate', 'critical', 'positive', 'negative', 'neutral', 'info'],
        `${recordLabel}.level`,
      );
    }
  });
}

/**
 * A ranked list read beside the ranked spatial map must name the same records:
 * a row without a map record, or a record without a row, would let the list
 * and the map highlight different things for one selection.
 */
export function validateRankedSpatialRecordIdentity(
  elements: readonly Record<string, unknown>[],
  path: string,
): void {
  const map = elements.find((element) => {
    if (element.element !== 'map') return false;
    const figure = element.figure as Record<string, unknown> | undefined;
    return figure?.kind === 'live' && figure.slotKey === REPORT_RANKED_SPATIAL_MAP_SLOT_KEY;
  });
  const list = elements.find((element) => element.element === 'rankedList');
  if (!map || !list) return;
  const slotProps = (map.figure as Record<string, unknown>).slotProps as Record<string, unknown>;
  const mapData = slotProps.mapData as Record<string, unknown> | null;
  if (!mapData) return;
  const rows = list.rows as Array<Record<string, unknown>>;
  const records = mapData.records as Array<Record<string, unknown>>;
  const rowIds = rows.map((row) => String(row.key ?? row.rank));
  const recordIds = records.map((record) => String(record.id));
  if (new Set(rowIds).size !== rowIds.length) {
    throw new Error(`Invalid descriptor: ${path} ranked list row identities must be unique.`);
  }
  if (rowIds.length !== recordIds.length || rowIds.some((rowId) => !recordIds.includes(rowId))) {
    throw new Error(
      `Invalid descriptor: ${path} ranked list rows and ranked map records must share the same identities.`,
    );
  }
}

function validateMeasurementMapContext(value: Record<string, unknown>, label: string): void {
  if (
    hasOwn(value, 'measurementSource') &&
    value.measurementSource !== null &&
    typeof value.measurementSource !== 'string'
  ) {
    throw new Error(`Invalid descriptor: ${label}.measurementSource must be string or null.`);
  }
  if (
    hasOwn(value, 'activeMeasurementCampaignId') &&
    value.activeMeasurementCampaignId !== null &&
    typeof value.activeMeasurementCampaignId !== 'string' &&
    typeof value.activeMeasurementCampaignId !== 'number'
  ) {
    throw new Error(
      `Invalid descriptor: ${label}.activeMeasurementCampaignId must be string, number, or null.`,
    );
  }
  if (
    hasOwn(value, 'baselineMetsakeskusDate') &&
    value.baselineMetsakeskusDate !== null &&
    typeof value.baselineMetsakeskusDate !== 'string'
  ) {
    throw new Error(`Invalid descriptor: ${label}.baselineMetsakeskusDate must be string or null.`);
  }
}

function validateMapSlotProps(
  slotKey: string,
  value: unknown,
  label: string,
  requiresMapData: boolean,
  requiresRankedSpatialData: boolean,
): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'propertyId',
      'caption',
      'snapshotSize',
      'selectedFeatureId',
      'selectedFeatureKind',
      'visibleFeatureIds',
      'onFeatureSelect',
      'onPlotSelect',
      'onStandSelect',
      'layerIds',
      'baseMapId',
      'interactionMode',
      'measurementSource',
      'activeMeasurementCampaignId',
      'baselineMetsakeskusDate',
      'measurementPlotRefs',
      'preview',
      'mapData',
      'sourceId',
      'workspaceFilters',
      'workspaceMode',
      'presentation',
    ],
    label,
  );
  if (value.presentation !== undefined) {
    validateMapPresentation(slotKey, value.presentation, `${label}.presentation`);
  }
  ['propertyId', 'caption', 'selectedFeatureId', 'sourceId'].forEach((key) =>
    validateOptionalString(value, key, label),
  );
  validateEnum(
    value.workspaceMode,
    ['find', 'orient', 'inspect', 'risk', 'refine'],
    `${label}.workspaceMode`,
    true,
  );
  if (value.workspaceFilters !== undefined) {
    assertRecord(value.workspaceFilters, `${label}.workspaceFilters`);
    Object.entries(value.workspaceFilters).forEach(([key, ids]) =>
      validateStringArray(ids, `${label}.workspaceFilters.${key}`),
    );
  }
  validateEnum(
    value.snapshotSize,
    ['preview', 'compact', 'standard', 'tall'],
    `${label}.snapshotSize`,
    true,
  );
  validateEnum(value.baseMapId, ['streets', 'satellite', 'terrain'], `${label}.baseMapId`, true);
  validateEnum(
    value.interactionMode,
    ['static', 'pan', 'select', 'explore'],
    `${label}.interactionMode`,
    true,
  );
  validateEnum(
    value.selectedFeatureKind,
    FM_MAP_SELECTED_FEATURE_KINDS,
    `${label}.selectedFeatureKind`,
    true,
  );
  validateOptionalCallback(value, 'onFeatureSelect', label);
  validateOptionalCallback(value, 'onPlotSelect', label);
  validateOptionalCallback(value, 'onStandSelect', label);
  if (requiresRankedSpatialData) {
    if (!hasOwn(value, 'mapData')) {
      throw new Error(
        `Invalid descriptor: ${label}.mapData is required for ranked spatial evidence.`,
      );
    }
    validateRankedSpatialMapData(value.mapData, `${label}.mapData`);
  } else {
    validateMapPreviewData(value, label, requiresMapData);
  }
  if (value.layerIds !== undefined) validateStringArray(value.layerIds, `${label}.layerIds`);
  if (value.visibleFeatureIds !== undefined)
    validateStringArray(value.visibleFeatureIds, `${label}.visibleFeatureIds`);
  if (value.measurementPlotRefs !== undefined) {
    assertArray(value.measurementPlotRefs, `${label}.measurementPlotRefs`);
    value.measurementPlotRefs.forEach((plot, index) => {
      const plotLabel = `${label}.measurementPlotRefs[${index}]`;
      assertRecord(plot, plotLabel);
      rejectUnknownKeys(plot, ['id', 'label', 'mappingStatus', 'standId'], plotLabel);
      assertNonEmptyString(plot.id, `${plotLabel}.id`);
      assertNonEmptyString(plot.label, `${plotLabel}.label`);
      validateEnum(
        plot.mappingStatus,
        FM_MEASUREMENT_PLOT_MAPPING_STATUSES,
        `${plotLabel}.mappingStatus`,
      );
      validateOptionalString(plot, 'standId', plotLabel);
    });
  }
  validateMeasurementMapContext(value, label);
}

export function validateLiveMapSlotProps(slotKey: string, slotProps: unknown, label: string): void {
  const requiresMapData = PRIMARY_MAP_DATA_SLOT_KEYS.has(slotKey);
  const requiresRankedSpatialData = slotKey === REPORT_RANKED_SPATIAL_MAP_SLOT_KEY;
  if (slotProps === undefined) {
    if (requiresMapData || requiresRankedSpatialData) {
      throw new Error(
        `Invalid descriptor: ${label} is required for governed map slot "${slotKey}".`,
      );
    }
    return;
  }
  validateMapSlotProps(slotKey, slotProps, label, requiresMapData, requiresRankedSpatialData);
}

export function validateMapSteps(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((step, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(step, itemLabel);
    rejectUnknownKeys(step, ['id', 'label', 'sublabel', 'disabled', 'testId'], itemLabel);
    assertNonEmptyString(step.id, `${itemLabel}.id`);
    assertNonEmptyString(step.label, `${itemLabel}.label`);
    validateOptionalString(step, 'sublabel', itemLabel);
    validateOptionalString(step, 'testId', itemLabel);
    validateOptionalBoolean(step, 'disabled', itemLabel);
  });
}

export function validateMapCallouts(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((callout, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(callout, itemLabel);
    rejectUnknownKeys(
      callout,
      ['id', 'label', 'value', 'unit', 'description', 'source', 'tone'],
      itemLabel,
    );
    assertNonEmptyString(callout.id, `${itemLabel}.id`);
    assertNonEmptyString(callout.label, `${itemLabel}.label`);
    if (
      hasOwn(callout, 'value') &&
      typeof callout.value !== 'string' &&
      typeof callout.value !== 'number'
    ) {
      throw new Error(`Invalid descriptor: ${itemLabel}.value must be string or number.`);
    }
    ['unit', 'description', 'source'].forEach((key) =>
      validateOptionalString(callout, key, itemLabel),
    );
    validateEnum(
      callout.tone,
      ['highlight', 'warning', 'success', 'neutral'],
      `${itemLabel}.tone`,
      true,
    );
  });
}

function validateMapLegendCategories(value: unknown, label: string): void {
  assertArray(value, `${label}.categories`);
  value.forEach((entry, index) => {
    const itemLabel = `${label}.categories[${index}]`;
    assertRecord(entry, itemLabel);
    rejectUnknownKeys(entry, ['id', 'label', 'speciesCode', 'feature'], itemLabel);
    assertNonEmptyString(entry.id, `${itemLabel}.id`);
    assertNonEmptyString(entry.label, `${itemLabel}.label`);
    const hasSpecies = entry.speciesCode !== undefined;
    const hasFeature = entry.feature !== undefined;
    if (hasSpecies === hasFeature) {
      throw new Error(
        `Invalid descriptor: ${itemLabel} names exactly one of a species code or a map feature.`,
      );
    }
    if (hasSpecies && !Number.isInteger(entry.speciesCode)) {
      throw new Error(`Invalid descriptor: ${itemLabel}.speciesCode must be a species code.`);
    }
    if (hasFeature) {
      validateEnum(entry.feature, FM_STORY_MAP_LEGEND_FEATURES, `${itemLabel}.feature`);
    }
  });
}

export function validateMapLegend(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    ['title', 'units', 'scale', 'paletteName', 'bins', 'thresholds', 'noDataLabel', 'categories'],
    label,
  );
  if (value.categories !== undefined) validateMapLegendCategories(value.categories, label);
  ['title', 'units', 'scale', 'paletteName', 'noDataLabel'].forEach((key) =>
    validateOptionalString(value, key, label),
  );
  if (value.thresholds !== undefined) {
    assertArray(value.thresholds, `${label}.thresholds`);
    value.thresholds.forEach((threshold, index) => {
      if (typeof threshold !== 'number' || !Number.isFinite(threshold)) {
        throw new Error(`Invalid descriptor: ${label}.thresholds[${index}] must be finite.`);
      }
    });
  }
  if (value.bins !== undefined) {
    assertArray(value.bins, `${label}.bins`);
    value.bins.forEach((bin, index) => {
      const itemLabel = `${label}.bins[${index}]`;
      assertRecord(bin, itemLabel);
      rejectUnknownKeys(bin, ['id', 'label', 'min', 'max', 'level'], itemLabel);
      assertNonEmptyString(bin.id, `${itemLabel}.id`);
      validateOptionalString(bin, 'label', itemLabel);
      validateOptionalFiniteNumber(bin, 'min', itemLabel);
      validateOptionalFiniteNumber(bin, 'max', itemLabel);
      validateEnum(
        bin.level,
        ['low', 'moderate', 'critical', 'positive', 'negative', 'neutral', 'info'],
        `${itemLabel}.level`,
      );
    });
  }
}
